import { Dispatch, SetStateAction, useEffect, useRef, useCallback } from 'react';
import * as Sentry from '@sentry/react';

import { ConnectorStatus } from '../common/enums/connectorStatus';
import { Status } from '../common/enums/status';
import { ocppService } from '../services/ocpp/OcppService';
import { Connector } from '../pages/Station/types';
import { SessionType } from '../components/common/Card/UserSessionCard/types';

const POLLING_INTERVAL_MS = 5000;

const ACTIVE_STATUSES: string[] = [
  ConnectorStatus.Preparing,
  ConnectorStatus.Charging,
  ConnectorStatus.Finishing,
];

interface UseChargingSessionPollingProps {
  stationId?: string;
  userId?: string | null;
  chosenConnector: Connector | null | false;
  // When false (WS disconnected), polling runs regardless of connector status.
  // Otherwise polling is gated to active statuses to avoid wasted requests.
  isWsConnected: boolean;
  onRefreshProgress: () => Promise<void>;
  onUpdateActiveSession: Dispatch<
    SetStateAction<SessionType | undefined | null>
  >;
}

/**
 * Polls meter values and session data via HTTP long polling.
 * Connector status is handled separately via WebSocket in Station.tsx.
 */
export const useChargingSessionPolling = ({
  stationId,
  userId,
  chosenConnector,
  isWsConnected,
  onRefreshProgress,
  onUpdateActiveSession,
}: UseChargingSessionPollingProps): void => {
  const connectorRef = useRef(chosenConnector);
  connectorRef.current = chosenConnector;

  const onRefreshProgressRef = useRef(onRefreshProgress);
  onRefreshProgressRef.current = onRefreshProgress;

  // While WS is down, the connector status may be stale — so we can't trust it
  // as a gate. Run polling whenever we have a selected connector and the WS is
  // not delivering updates, so REST keeps the session in sync until WS recovers.
  const isActive = !!(
    stationId &&
    userId &&
    chosenConnector &&
    (ACTIVE_STATUSES.includes(chosenConnector.status as string) || !isWsConnected)
  );

  const refreshSessionData = useCallback(async () => {
    const connector = connectorRef.current;
    if (!stationId || !connector) return;

    // Poll meter values / session progress
    try {
      await onRefreshProgressRef.current();
    } catch (error) {
      Sentry.captureException({
        message: '[Station][setChargingProcess][interval]',
        error,
      });
    }

    // Poll last session for active charging state
    try {
      const session = await ocppService.getLastUserSession(stationId);
      if (session?.chargingStatus === Status.CHARGING) {
        onUpdateActiveSession(session);
      }
    } catch (error) {
      Sentry.captureException({
        message: '[ocppService][getLastUserSession][interval]',
        error,
      });
    }
  }, [stationId, onUpdateActiveSession]);

  useEffect(() => {
    if (!isActive) return;

    refreshSessionData();

    const intervalId = window.setInterval(refreshSessionData, POLLING_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [isActive, refreshSessionData]);
};
